import { useEffect, useState, useCallback } from 'react';
import { useStudent } from '../context/StudentContext';
import { scoresApi, recommendApi, type Score, type RecommendResult } from '../api';
import { TrendingUp, Star, School, ClipboardList, Target, Calendar, Award, ChevronRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import PullToRefresh from '../components/PullToRefresh';

type PageId = 'dashboard' | 'students' | 'scores' | 'schools' | 'majors' | 'recommendation' | 'volunteer' | 'preferences' | 'data-admin';

interface Props {
  onNavigate: (page: PageId) => void;
}

// 科目颜色配置
const SENIOR_SUBJECT_COLORS: Record<string, string> = {
  '语文': '#EF4444', '数学': '#F97316', '英语': '#22C55E',
  '物理': '#3B82F6', '化学': '#A855F7', '地理': '#06B6D4',
  '历史': '#EC4899', '生物': '#14B8A6', '政治': '#F59E0B',
};

const JUNIOR_SUBJECT_COLORS: Record<string, string> = {
  '语文': '#EF4444', '数学': '#F97316', '英语': '#22C55E',
  '物理': '#3B82F6', '化学': '#A855F7', '道德与法治': '#F59E0B',
  '历史': '#EC4899', '地理': '#06B6D4', '生物学': '#14B8A6', '体育': '#8B5CF6',
};

// 根据 graduation_year 计算考试日期
function getExamDates(educationLevel: 'senior' | 'junior', grade: string, graduationYear?: number) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  // 如果没有 graduationYear，根据年级动态计算
  if (!graduationYear) {
    const academicStartYear = month >= 9 ? year : year - 1;
    let gradeNum = 1;
    if (grade.includes('三') || grade.includes('3') || grade.includes('9')) gradeNum = 3;
    else if (grade.includes('二') || grade.includes('2') || grade.includes('8')) gradeNum = 2;
    graduationYear = academicStartYear + (4 - gradeNum);
  }

  if (educationLevel === 'senior') {
    return { label: '距高考', date: new Date(`${graduationYear}-06-07`), dateLabel: `${graduationYear}年6月7日` };
  }

  // 初中：初三→中考，初一初二→学期结束
  const isGrade9 = grade.includes('三') || grade.includes('9');
  if (isGrade9) {
    return { label: '距中考', date: new Date(`${graduationYear}-06-20`), dateLabel: `${graduationYear}年6月20日` };
  }
  // 初一/初二——显示距中考
  return { label: '距中考', date: new Date(`${graduationYear}-06-20`), dateLabel: `${graduationYear}年6月20日` };
}

export default function Dashboard({ onNavigate }: Props) {
  const { currentStudent } = useStudent();
  const [scores, setScores] = useState<Score[]>([]);
  const [recommendation, setRecommendation] = useState<RecommendResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [subjectConfigs, setSubjectConfigs] = useState<Array<{ key: string; name: string; max: number }>>([]);

  const educationLevel = currentStudent?.education_level || 'senior';
  const examInfo = getExamDates(educationLevel, currentStudent?.grade || '', currentStudent?.graduation_year);
  const daysLeft = Math.ceil((examInfo.date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  // 根据学段选择科目颜色
  const subjectColors = educationLevel === 'junior' ? JUNIOR_SUBJECT_COLORS : SENIOR_SUBJECT_COLORS;
  const subjectKeys = educationLevel === 'junior'
    ? ['chinese_score', 'math_score', 'english_score', 'physics_score', 'chemistry_score', 'politics_score', 'history_score', 'geography_score', 'biology_score']
    : ['chinese_score', 'math_score', 'english_score', 'physics_score', 'chemistry_score', 'geography_score', 'history_score', 'biology_score', 'politics_score'];

  useEffect(() => {
    if (!currentStudent) return;
    loadData();
  }, [currentStudent?.id]);

  const loadData = useCallback(async () => {
    if (!currentStudent) return;
    setLoading(true);
    try {
      const [scoresRes, subjectsRes, recRes] = await Promise.all([
        scoresApi.trend(currentStudent.id),
        scoresApi.subjects(currentStudent.id).catch(() => null),
        recommendApi.byStudent(currentStudent.id).catch(() => null),
      ]);
      setScores(scoresRes.data.data || []);
      if (subjectsRes?.data?.data) {
        setSubjectConfigs(subjectsRes.data.data);
      }
      if (recRes) setRecommendation(recRes.data.data);
    } finally {
      setLoading(false);
    }
  }, [currentStudent?.id]);

  const latestScore = scores[scores.length - 1];

  // 基础图表数据（总分）
  const baseChartData = scores.slice(-8).map(s => ({
    name: s.exam_name.length > 6 ? s.exam_name.slice(0, 6) : s.exam_name,
    总分: s.total_score,
  }));

  // 按学段添加科目数据
  const chartData = baseChartData.map(row => {
    const extended: Record<string, number | string | undefined> = { ...row };
    subjectKeys.forEach(key => {
      const scoreKey = key as keyof Score;
      const val = (latestScore as any)?.[scoreKey];
      if (val != null) {
        // 从科目配置获取名称
        const config = subjectConfigs.find(c => c.key === key);
        const name = config?.name || key.replace('_score', '');
        extended[name] = val;
      }
    });
    return extended;
  });

  // 为图表中的每个科目分配颜色
  const getSubjectColor = (name: string) => subjectColors[name] || '#6B7280';

  return (
    <PullToRefresh onRefresh={loadData}>
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">
          {educationLevel === 'junior' ? '升学规划总览' : '升学规划总览'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {currentStudent?.name} · {currentStudent?.school} · {currentStudent?.grade} · {currentStudent?.subject_type}
          {educationLevel === 'junior' && ' · 初中'}
        </p>
      </div>

      {/* 核心指标卡片 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <StatCard
          icon={<Target className="text-blue-500" size={22} />}
          label="最新总分"
          value={latestScore ? `${latestScore.total_score}分` : '暂无数据'}
          sub={latestScore ? `年级排名 ${latestScore.grade_rank}` : '请录入成绩'}
          color="bg-blue-50 border-blue-100"
        />
        <StatCard
          icon={<TrendingUp className="text-purple-500" size={22} />}
          label={educationLevel === 'senior' ? '预测省位次' : '预测市位次'}
          value={latestScore?.province_rank_estimate ? `第 ${latestScore.province_rank_estimate.toLocaleString()} 名` : '待估算'}
          sub={latestScore ? latestScore.exam_name : '基于最新成绩'}
          color="bg-purple-50 border-purple-100"
        />
        <StatCard
          icon={<Calendar className="text-amber-500" size={22} />}
          label={examInfo.label}
          value={`${daysLeft} 天`}
          sub={examInfo.dateLabel}
          color="bg-amber-50 border-amber-100"
        />
        <StatCard
          icon={<Award className="text-green-500" size={22} />}
          label="已录入成绩"
          value={`${scores.length} 次`}
          sub={`目标：${currentStudent?.target_level || (educationLevel === 'senior' ? '985/211' : '普高')}`}
          color="bg-green-50 border-green-100"
        />
      </div>

      {/* 主内容区 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* 成绩趋势图 */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-3 md:p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">成绩趋势</h2>
            <button onClick={() => onNavigate('scores')}
              className="text-blue-500 text-sm hover:underline flex items-center gap-1">
              查看详情 <ChevronRight size={14} />
            </button>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="score" domain={['auto', 'auto']} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: number) => v?.toLocaleString()} />
                <Legend />
                <Line yAxisId="score" type="monotone" dataKey="总分" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 4 }} />
                {subjectConfigs.map(config => {
                  const hasData = chartData.some(d => (d as any)[config.name] != null);
                  if (!hasData) return null;
                  return (
                    <Line
                      key={config.key}
                      yAxisId="score"
                      type="monotone"
                      dataKey={config.name}
                      stroke={getSubjectColor(config.name)}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      strokeDasharray="4 2"
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex flex-col items-center justify-center text-gray-400">
              <TrendingUp size={40} className="opacity-30 mb-3" />
              <p className="text-sm">暂无成绩数据</p>
              <button onClick={() => onNavigate('scores')}
                className="mt-3 text-blue-500 text-sm hover:underline">去录入成绩 →</button>
            </div>
          )}
        </div>

        {/* 快速推荐预览 */}
        <div className="bg-white rounded-2xl p-3 md:p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">
              {educationLevel === 'junior' ? '推荐高中' : '推荐院校'}
            </h2>
            <button onClick={() => onNavigate('recommendation')}
              className="text-blue-500 text-sm hover:underline flex items-center gap-1">
              查看全部 <ChevronRight size={14} />
            </button>
          </div>
          {recommendation ? (
            <div className="space-y-2">
              {recommendation.education_level === 'junior' ? (
                // 中考：显示高中推荐
                <>
                  {(recommendation.wen as any[]).slice(0, 3).map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-green-50 rounded-xl border border-green-100">
                      <span className="w-6 h-6 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center">稳</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{s.name}</div>
                        <div className="text-xs text-gray-500">{s.district} · 分数线 {s.score_line}</div>
                      </div>
                      {s.is_demonstration === 1 && <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded flex-shrink-0">示范</span>}
                      {s.school_type === '职高' && <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded flex-shrink-0">职高</span>}
                    </div>
                  ))}
                  {(recommendation.chong as any[]).slice(0, 2).map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-xl border border-amber-100">
                      <span className="w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">冲</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{s.name}</div>
                        <div className="text-xs text-gray-500">{s.district} · 分数线 {s.score_line}</div>
                      </div>
                      {s.is_demonstration === 1 && <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded flex-shrink-0">示范</span>}
                    </div>
                  ))}
                  <div className="text-center text-xs text-gray-400 pt-1">
                    基于分数 {recommendation.student_score}
                  </div>
                </>
              ) : (
                // 高考：显示大学推荐
                <>
                  {(recommendation.wen as any[]).slice(0, 3).map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-green-50 rounded-xl border border-green-100">
                      <span className="w-6 h-6 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center">稳</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{s.school_name}</div>
                        <div className="text-xs text-gray-500">{s.city} · 均位次 {s.avg_rank?.toLocaleString()}</div>
                      </div>
                      {s.is985 === 1 && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded flex-shrink-0">985</span>}
                      {s.is985 !== 1 && s.is211 === 1 && <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded flex-shrink-0">211</span>}
                    </div>
                  ))}
                  {(recommendation.chong as any[]).slice(0, 2).map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-xl border border-amber-100">
                      <span className="w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">冲</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{s.school_name}</div>
                        <div className="text-xs text-gray-500">{s.city} · 均位次 {s.avg_rank?.toLocaleString()}</div>
                      </div>
                      {s.is985 === 1 && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded flex-shrink-0">985</span>}
                    </div>
                  ))}
                  <div className="text-center text-xs text-gray-400 pt-1">
                    基于预测省位次 {recommendation.province_rank?.toLocaleString()}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-400">
              <Star size={36} className="opacity-30 mb-2" />
              <p className="text-sm">录入成绩后可查看推荐</p>
            </div>
          )}
        </div>
      </div>

      {/* 快捷操作 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-4 md:mt-6">
        <QuickAction icon={<TrendingUp size={20} />} label="录入成绩" desc="记录本次考试成绩" color="from-blue-500 to-blue-600" onClick={() => onNavigate('scores')} />
        <QuickAction icon={<Star size={20} />} label="查看推荐" desc={educationLevel === 'junior' ? '冲稳保三档高中' : '冲稳保三档院校'} color="from-amber-500 to-orange-500" onClick={() => onNavigate('recommendation')} />
        <QuickAction icon={<School size={20} />} label={educationLevel === 'junior' ? '探索高中' : '探索院校'} desc={educationLevel === 'junior' ? '浏览重点高中数据' : '浏览985/211数据'} color="from-green-500 to-emerald-500" onClick={() => onNavigate('schools')} />
        <QuickAction icon={<ClipboardList size={20} />} label="志愿模拟" desc="管理填报志愿单" color="from-purple-500 to-violet-500" onClick={() => onNavigate('volunteer')} />
      </div>
    </div>
    </PullToRefresh>
  );
}

function StatCard({ icon, label, value, sub, color }: {
  icon: React.ReactNode; label: string; value: string; sub: string; color: string;
}) {
  return (
    <div className={`${color} rounded-2xl p-3 md:p-4 border`}>
      <div className="flex items-center gap-2 mb-1 md:mb-2">{icon}<span className="text-xs text-gray-500">{label}</span></div>
      <div className="text-lg md:text-xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div>
    </div>
  );
}

function QuickAction({ icon, label, desc, color, onClick }: {
  icon: React.ReactNode; label: string; desc: string; color: string; onClick: () => void;
}) {
  return (
    <button onClick={onClick}
      className={`bg-gradient-to-r ${color} text-white rounded-2xl p-3 md:p-4 text-left hover:opacity-90 active:scale-95 transition-all shadow-sm`}>
      <div className="mb-2 opacity-80">{icon}</div>
      <div className="font-semibold text-sm">{label}</div>
      <div className="text-xs opacity-75 mt-0.5">{desc}</div>
    </button>
  );
}
